import { Box, Group, MultiSelect, Select, Slider, Text } from "@mantine/core"
import { DateInput } from "@mantine/dates";
import dayjs from "dayjs";
import { LEAGUE } from "../../models/NBA";
import { useContext, useEffect, useCallback, useMemo } from "react";
import { NBAInsightsContext } from "../../context/global-state";
import { useLeague } from "../../context/LeagueContext";

import { useForm, Controller } from "react-hook-form";
import { Season, SeasonType } from "../../client";
import { PlayerSearchData, VideoSearchRanges } from "../../models/SearchData";

import { defaultPlayer } from "../../utils/utils";
import { SeasonTypeSchema, StatSchema } from "../../client/schemas.gen";
import { getPlayerSeasons, filterPlayersWithVideos } from "../../models/SearchData"
import SearchFormActions from "./SearchFormActions";

interface PlayerSearchFormData {
  submitText: string,
  newSearchText?: string,
  lastSearch: PlayerSearchData | null,
  onSubmit: (formData: PlayerSearchData) => void,
  onAddToStack?: (formData: PlayerSearchData) => void,
  onNewSearch?: (formData: PlayerSearchData) => void,
  isStackMode?: boolean,
  isStackFull?: boolean,
  isEditing?: boolean,
  onUpdateInStack?: (formData: PlayerSearchData) => void,
  onCancelEdit?: () => void,
}

const fields = ["player", "seasons", "seasonType", "lastNGames", "dateRange", "periods", "oppTeam", "statCategories"]

const getSeasonDateBounds = (league: LEAGUE, season?: Season): { minDate?: Date; maxDate?: Date } => {
  if (!season) return {}
  if (league === LEAGUE.NBA) {
    if (season === "2019-20") {
      return {
        minDate: dayjs("2019-09-01").toDate(),
        maxDate: dayjs("2020-10-31").toDate(),
      }
    }
    if (season === "2020-21") {
      return {
        minDate: dayjs("2020-11-01").toDate(),
        maxDate: dayjs("2021-08-15").toDate(),
      }
    }
    const startYear = Number(season.slice(0, 4))
    if (!isNaN(startYear)) {
      return {
        minDate: dayjs(`${startYear}-09-01`).toDate(),
        maxDate: dayjs(`${startYear + 1}-07-31`).toDate(),
      }
    }
  } else if (league === LEAGUE.WNBA) {
    if (season === "2020") {
      return {
        minDate: dayjs("2020-05-01").toDate(),
        maxDate: dayjs("2020-11-30").toDate(),
      }
    }
    const year = Number(season.slice(0, 4))
    if (!isNaN(year)) {
      return {
        minDate: dayjs(`${year}-04-01`).toDate(),
        maxDate: dayjs(`${year}-11-30`).toDate(),
      }
    }
  }
  return {}
}

const PlayerSearchForm: React.FC<PlayerSearchFormData> = ({
  submitText,
  newSearchText,
  lastSearch,
  onSubmit,
  onAddToStack,
  onNewSearch,
  isStackMode = false,
  isStackFull = false,
  isEditing = false,
  onUpdateInStack,
  onCancelEdit,
}) => {
  const { nbaPlayers, wnbaPlayers, nbaTeams, wnbaTeams } = useContext(NBAInsightsContext)
  const { league } = useLeague()

  const currentPlayers = league === LEAGUE.NBA ? nbaPlayers : wnbaPlayers
  const filteredPlayers = useMemo(() => {
    return filterPlayersWithVideos(league, currentPlayers)
  }, [league, currentPlayers])

  const currentTeams = league === LEAGUE.NBA ? nbaTeams : wnbaTeams

  const defaultSeasonType: SeasonType = "Regular Season"

  const getDefaultValues = useCallback((): PlayerSearchData => {
    if (lastSearch && lastSearch.league === league) {
      return lastSearch
    }
    return {
      league: league,
      player: defaultPlayer(filteredPlayers, league),
      lastNGames: "0",
      seasons: [VideoSearchRanges[league][defaultSeasonType][0]],
      seasonType: defaultSeasonType,
      oppTeamId: "0",
      categories: [StatSchema.enum.find(v => v === "PTS")!],
      dateFrom: "",
      dateTo: "",
      periods: [],
    }
  }, [lastSearch, league, filteredPlayers])

  // Form default values
  const { handleSubmit, control, reset, watch, setValue } = useForm<PlayerSearchData>({
    defaultValues: getDefaultValues(),
  })

  // Synchronize form when lastSearch (derived from URL SoT), league, or loaded players change
  useEffect(() => {
    reset(getDefaultValues())
  }, [getDefaultValues, reset])

  const watchedSeasonType = watch("seasonType") || defaultSeasonType
  const availableSeasons = VideoSearchRanges[league]?.[watchedSeasonType] || VideoSearchRanges[league]["Regular Season"]
  const currentPlayer = watch("player")

  const playerSeasons = useMemo(() => {
    return getPlayerSeasons(currentPlayer, availableSeasons)
  }, [currentPlayer, availableSeasons])

  useEffect(() => {
    if (!currentPlayer || playerSeasons.length === 0) return
    const currentSelected = watch("seasons") || []
    const valid = currentSelected.filter((s) => playerSeasons.includes(s))
    if (valid.length === 0) {
      setValue("seasons", [playerSeasons[0]])
    } else if (valid.length !== currentSelected.length) {
      setValue("seasons", valid)
    }
  }, [currentPlayer, playerSeasons, setValue, watch])

  const seasonsValue = watch("seasons")
  const isSingleSeason = (seasonsValue || []).length === 1
  const selectedSeason = isSingleSeason ? seasonsValue?.[0] : undefined
  const seasonDateBounds = useMemo(() => getSeasonDateBounds(league, selectedSeason), [league, selectedSeason])

  const watchedDateFrom = watch("dateFrom")
  const watchedDateTo = watch("dateTo")

  // Reset advanced single-season filters when multiple seasons are selected
  useEffect(() => {
    if (seasonsValue && seasonsValue.length > 1) {
      if (watch("lastNGames") !== "0") {
        setValue("lastNGames", "0")
      }
      if (watch("dateFrom") !== "") {
        setValue("dateFrom", "")
      }
      if (watch("dateTo") !== "") {
        setValue("dateTo", "")
      }
    }
  }, [seasonsValue, setValue, watch])

  const playerComponent = (
    <Controller
      name="player"
      control={control}
      render={({ field }) => (
        <Select
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10
            }
          }}
          data-testid={"player"}
          label="Select player"
          placeholder={filteredPlayers.length === 0 ? "Loading players..." : "Select player"}
          disabled={filteredPlayers.length === 0}
          onChange={(value) => field.onChange(filteredPlayers.find(player => player.fullName === value))}
          value={field.value?.fullName}
          data={filteredPlayers.map(player => player.fullName)}
          allowDeselect={false}
          searchable={true}
        />
      )}
    />
  )

  const seasonsComponent = (
    <Controller
      name="seasons"
      control={control}
      render={({field}) => (
        <MultiSelect 
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10
            }
          }}
          data-testid={"seasons"}
          label="Seasons"
          data={playerSeasons}
          value={field.value || []}
          onChange={(value) => field.onChange(value)}
        />
      )}
    />
  )

  const seasonTypeComponent = (
    <Controller
      name="seasonType"
      control={control}
      render={({field}) => (
        <Select 
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10
            }
          }}
          data-testid={"seasonType"}
          label="Season Type"
          data={Object.values(SeasonTypeSchema.enum)}
          value={field.value}
          onChange={(value) => field.onChange(value)}
        />
      )}
    />
  )

  const isWnba = league === LEAGUE.WNBA
  const maxLastNGames = isWnba ? 44 : 82
  const lastNGamesMarks = isWnba
    ? [
        { value: 0, label: "All" },
        { value: 10, label: "10" },
        { value: 20, label: "20" },
        { value: 30, label: "30" },
        { value: 44, label: "44" },
      ]
    : [
        { value: 0, label: "All" },
        { value: 10, label: "10" },
        { value: 20, label: "20" },
        { value: 40, label: "40" },
        { value: 82, label: "82" },
      ]

  useEffect(() => {
    const currentVal = Number(watch("lastNGames")) || 0
    if (currentVal > maxLastNGames) {
      setValue("lastNGames", String(maxLastNGames))
    }
  }, [maxLastNGames, setValue, watch])

  const lastNGamesComponent = (
    <Controller
      name="lastNGames"
      control={control}
      render={({ field }) => (
        <Box
          style={{
            padding: 10,
            paddingTop: 5,
            paddingBottom: 0,
            opacity: isSingleSeason ? 1 : 0.6,
          }}
        >
          <Text size="sm" fw={500} mb={6}>
            Recent games: {isSingleSeason && Number(field.value) > 0 ? field.value : "All"}
            {!isSingleSeason && (
              <Text span c="dimmed" size="xs" fw={400}>
                {" "}(Single season only)
              </Text>
            )}
          </Text>
          <Slider
            data-testid={"lastNGames"}
            value={isSingleSeason ? (Number(field.value) || 0) : 0}
            onChange={(value) => field.onChange(String(value))}
            min={0}
            max={maxLastNGames}
            step={1}
            disabled={!isSingleSeason}
            marks={lastNGamesMarks}
            mb="xs"
          />
        </Box>
      )}
    />
  )

  const oppTeamComponent = (
    <Controller
      name="oppTeamId"
      control={control}
      render={({field}) => (
        <Select
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10
            }
          }}
          data-testid={"oppTeamId"}
          label="Opposing team"
          data={currentTeams.map(team => ({ value: team.id, label: team.fullName }))}
          value={field.value || "0"}
          onChange={(value) => field.onChange(value)}
          searchable
        />
      )}
    />
  )

  const categoriesComponent = (
    <Controller
      name="categories"
      control={control}
      render={({field}) => (
        <MultiSelect 
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10
            }
          }}
          data-testid={"categories"}
          label="Stat categories"
          placeholder="Add a stat category"
          data={Object.values(StatSchema.enum)}
          value={field.value || []}
          onChange={(value) => field.onChange(value)}
          searchable
        />
      )}
    />
  )

  const dateRangeComponent = (
    <Box
      style={{
        padding: 10,
        paddingTop: 5,
        paddingBottom: 0,
      }}
    >
      <Group grow gap="xs">
        <Controller
          name="dateFrom"
          control={control}
          render={({ field }) => (
            <DateInput
              {...field}
              key={`${selectedSeason || 'all'}-dateFrom`}
              styles={{
                input: {
                  borderRadius: 10,
                },
              }}
              data-testid={"dateFrom"}
              label="Date from"
              placeholder={isSingleSeason ? "YYYY-MM-DD" : "Single season only"}
              valueFormat="YYYY-MM-DD"
              disabled={!isSingleSeason}
              defaultDate={seasonDateBounds.minDate}
              minDate={seasonDateBounds.minDate}
              maxDate={watchedDateTo && dayjs(watchedDateTo).isValid() ? dayjs(watchedDateTo).toDate() : seasonDateBounds.maxDate}
              value={field.value && dayjs(field.value).isValid() ? dayjs(field.value).toDate() : null}
              onChange={(date) => {
                field.onChange(date && dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "")
              }}
              clearable
            />
          )}
        />
        <Controller
          name="dateTo"
          control={control}
          render={({ field }) => (
            <DateInput
              {...field}
              key={`${selectedSeason || 'all'}-dateTo`}
              styles={{
                input: {
                  borderRadius: 10,
                },
              }}
              data-testid={"dateTo"}
              label="Date to"
              placeholder={isSingleSeason ? "YYYY-MM-DD" : "Single season only"}
              valueFormat="YYYY-MM-DD"
              disabled={!isSingleSeason}
              defaultDate={seasonDateBounds.maxDate}
              minDate={watchedDateFrom && dayjs(watchedDateFrom).isValid() ? dayjs(watchedDateFrom).toDate() : seasonDateBounds.minDate}
              maxDate={seasonDateBounds.maxDate}
              value={field.value && dayjs(field.value).isValid() ? dayjs(field.value).toDate() : null}
              onChange={(date) => {
                field.onChange(date && dayjs(date).isValid() ? dayjs(date).format("YYYY-MM-DD") : "")
              }}
              clearable
            />
          )}
        />
      </Group>
    </Box>
  )

  const periodOptions = [
    { value: "1", label: "Q1" },
    { value: "2", label: "Q2" },
    { value: "3", label: "Q3" },
    { value: "4", label: "Q4" },
    { value: "5", label: "OT" },
  ]

  const periodsComponent = (
    <Controller
      name="periods"
      control={control}
      render={({ field }) => (
        <MultiSelect
          {...field}
          styles={{
            root: {
              padding: 10,
              paddingTop: 5,
              paddingBottom: 0,
            },
            input: {
              borderRadius: 10,
            },
          }}
          data-testid={"periods"}
          label="Quarter / Period"
          placeholder={field.value && field.value.length > 0 ? undefined : "All Quarters"}
          data={periodOptions}
          value={(field.value || []).map(String)}
          onChange={(values) => {
            field.onChange(values.map(Number))
          }}
          clearable
        />
      )}
    />
  )

  const components: { [key: string]: JSX.Element; } = {
    "player": playerComponent,
    "seasons": seasonsComponent,
    "seasonType": seasonTypeComponent,
    "lastNGames": lastNGamesComponent,
    "dateRange": dateRangeComponent,
    "periods": periodsComponent,
    "oppTeam": oppTeamComponent,
    "statCategories": categoriesComponent,
  }


  return ( <>
    <form id="search-filter-form" onSubmit={handleSubmit(onSubmit)}>
      {fields.map((field: string, idx) => <div key={idx}>{components[field]}</div>)}

      <SearchFormActions
        submitText={submitText}
        newSearchText={newSearchText}
        isStackMode={isStackMode}
        isStackFull={isStackFull}
        isEditing={isEditing}
        disabled={filteredPlayers.length === 0}
        loading={filteredPlayers.length === 0}
        loadingText="Loading players..."
        onAddToStack={onAddToStack ? handleSubmit(onAddToStack) : undefined}
        onUpdateInStack={handleSubmit(onUpdateInStack || onSubmit)}
        onCancelEdit={onCancelEdit}
        onNewSearch={handleSubmit(onNewSearch || onSubmit)}
      />
    </form>
  </>)
}

export default PlayerSearchForm